import type {
  LeadFormFieldDto,
  ProfileModuleDto,
} from '@up2stay/up2promo-api-client';
import {
  LeadFormFieldType,
  ProfileButtonShape,
  ProfileHeaderStyle,
  ProfileLayout,
  ProfileTheme,
} from '@up2stay/up2promo-api-client';
import { z } from 'zod';

import {
  PROFILE_STYLING_FORM_DEFAULTS,
  type ProfileHeaderImageValue,
} from '@/core/lib/profile-styling-form';

export const PROFILE_EDIT_MAX_JOB_TITLE = 30;
export const PROFILE_EDIT_MAX_COMPANY = 30;
export const PROFILE_EDIT_MAX_LOCATION = 30;

const headerImageSchema = z
  .object({
    id: z.string().min(1),
    path: z.string().optional(),
  })
  .nullable();

export const profileFormSchema = z.object({
  jobTitle: z.string().max(PROFILE_EDIT_MAX_JOB_TITLE).optional().nullable(),
  company: z.string().max(PROFILE_EDIT_MAX_COMPANY).optional().nullable(),
  location: z.string().max(PROFILE_EDIT_MAX_LOCATION).optional().nullable(),
  showProfileImage: z.boolean().optional(),
  language: z.enum(['auto', 'de', 'en']).optional(),
  visibility: z.enum(['private', 'unlisted']).optional(),
  leadFormEnabled: z.boolean().optional(),
  leadFormVisibilityMode: z.enum(['off', 'manual', 'auto_open']).optional(),
  leadFormAutoOpenDelay: z.number().min(0).max(15).optional().nullable(),
  leadsAutoShareWithWorkspace: z.boolean().optional(),
  showUp2promoBranding: z.boolean().optional(),
  accentColor: z.string().nullable().optional(),
  layout: z.nativeEnum(ProfileLayout).optional(),
  theme: z.nativeEnum(ProfileTheme).optional(),
  headerStyle: z.nativeEnum(ProfileHeaderStyle).optional(),
  headerImage: headerImageSchema.optional(),
  buttonShape: z.nativeEnum(ProfileButtonShape).nullable().optional(),
});

export type ProfileFormData = z.infer<typeof profileFormSchema> & {
  headerImage?: ProfileHeaderImageValue;
};

export const PROFILE_EDIT_DEFAULT_VALUES: ProfileFormData = {
  jobTitle: null,
  company: null,
  location: null,
  showProfileImage: true,
  language: 'auto',
  visibility: 'unlisted',
  leadFormEnabled: false,
  leadFormVisibilityMode: 'off',
  leadFormAutoOpenDelay: 0,
  leadsAutoShareWithWorkspace: false,
  showUp2promoBranding: true,
  ...PROFILE_STYLING_FORM_DEFAULTS,
};

export function areProfileFormValuesEqual(
  a: ProfileFormData,
  b: ProfileFormData | null,
): boolean {
  if (!b) return false;
  return (
    a.jobTitle === b.jobTitle &&
    a.company === b.company &&
    a.location === b.location &&
    a.showProfileImage === b.showProfileImage &&
    a.language === b.language &&
    a.visibility === b.visibility &&
    a.leadFormEnabled === b.leadFormEnabled &&
    a.leadFormVisibilityMode === b.leadFormVisibilityMode &&
    a.leadFormAutoOpenDelay === b.leadFormAutoOpenDelay &&
    a.leadsAutoShareWithWorkspace === b.leadsAutoShareWithWorkspace &&
    a.showUp2promoBranding === b.showUp2promoBranding &&
    a.accentColor === b.accentColor &&
    a.layout === b.layout &&
    a.theme === b.theme &&
    a.headerStyle === b.headerStyle &&
    (a.headerImage?.id ?? null) === (b.headerImage?.id ?? null) &&
    a.buttonShape === b.buttonShape
  );
}

export function areProfileModulesEqual(
  a: ProfileModuleDto[],
  b: ProfileModuleDto[] | null,
): boolean {
  if (!b) return false;
  if (a.length !== b.length) return false;
  return a.every((module, index) => {
    const otherModule = b[index];
    if (!otherModule) return false;
    return (
      module.type === otherModule.type &&
      module.enabled === otherModule.enabled &&
      module.order === otherModule.order &&
      JSON.stringify(module.config) === JSON.stringify(otherModule.config)
    );
  });
}

export function buildFixedLeadFormFields(
  fieldLabels: Record<string, string> | undefined,
): LeadFormFieldDto[] {
  const labels = fieldLabels ?? {};
  const label = (key: string, fallback: string) => labels[key] ?? fallback;

  return [
    {
      type: LeadFormFieldType.FIRST_NAME,
      label: label('firstName', 'First name'),
      required: true,
      order: 0,
    },
    {
      type: LeadFormFieldType.LAST_NAME,
      label: label('lastName', 'Last name'),
      required: false,
      order: 1,
    },
    {
      type: LeadFormFieldType.PHONE,
      label: label('phone', 'Phone'),
      required: true,
      order: 2,
    },
    {
      type: LeadFormFieldType.EMAIL,
      label: label('email', 'Email'),
      required: true,
      order: 3,
    },
    {
      type: LeadFormFieldType.COMPANY,
      label: label('workspace', 'Company'),
      required: false,
      order: 4,
    },
    {
      type: LeadFormFieldType.JOB_TITLE,
      label: label('jobTitle', 'Job title'),
      required: false,
      order: 5,
    },
  ];
}
